import DialogContent, { type DialogContentProps } from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import { type FC, type PropsWithChildren, type ReactNode } from 'react'
import IconButton from '@mui/material/IconButton'
import Close from '@mui/icons-material/Close'
import Dialog, { type DialogProps } from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import LinearProgress from '@mui/material/LinearProgress'
import Divider from '@mui/material/Divider'

interface ModalProps extends Omit<DialogProps, 'open'> {
	title: string
	isOpen: boolean
	actions?: ReactNode
	footerContent?: ReactNode
	cancelLabel?: string
	withCancelButton?: boolean
	disableClose?: boolean
	isLoading?: boolean
	ContentProps?: DialogContentProps
	onClose(): void
}

export const Modal: FC<PropsWithChildren<ModalProps>> = ({
	title,
	isOpen,
	onClose,
	disableClose,
	isLoading,
	children,
	actions,
	footerContent,
	withCancelButton = true,
	cancelLabel = 'Cancel',
	fullWidth = false,
	maxWidth = 'sm',
	ContentProps,
	...rest
}) => {
	return (
		<Dialog open={isOpen} onClose={!disableClose ? onClose : undefined} fullWidth={fullWidth} maxWidth={maxWidth} {...rest}>
			<DialogTitle>{title}</DialogTitle>
			<IconButton
				disabled={disableClose}
				onClick={onClose}
				size='small'
				aria-haspopup='true'
				sx={{
					position: 'absolute',
					right: 8,
					top: 8,
					color: theme => theme.palette.grey[500],
				}}
			>
				<Close />
			</IconButton>
			<DialogContent sx={{ pt: 0, ...ContentProps?.sx }} {...ContentProps}>
				{children}
			</DialogContent>
			{footerContent}
			{isLoading && <LinearProgress variant='indeterminate' sx={{ height: 3, pb: actions || withCancelButton ? 0.4 : 0.7 }} />}
			{(actions || withCancelButton) && (
				<>
					<Divider variant='fullWidth' orientation='horizontal' />
					<DialogActions>
						{withCancelButton && (
							<Button variant='outlined' onClick={onClose} disabled={isLoading} sx={{ mr: 1 }} color='inherit'>
								{cancelLabel}
							</Button>
						)}
						{actions}
					</DialogActions>
				</>
			)}
		</Dialog>
	)
}
